import type { shopModeApi } from "../../api/shopMode";
import { t as translate } from "../../i18n/t";
import { money } from "../../i18n/intl";
import {
  defaultOrderWhen,
  orderWhenPayload,
  type OrderWhen,
} from "./orderWhen";

/**
 * G-112 (OD-94): the till's "Schedule order" — the ticket becomes an ORDER.
 * The rules live here, apart from the form, so they can be tested.
 */

/** How much the customer pays at the till when the order is scheduled. */
export type SchedulePayment = "NONE" | "PART" | "FULL";

/** The till's "Schedule order" form state (strings for controlled inputs). */
export interface ScheduleDraft {
  enabled: boolean;
  fulfillment: "PICKUP" | "COURIER";
  recipientName: string;
  recipientPhone: string;
  when: OrderWhen;
  payment: SchedulePayment;
  partAmountEur: string;
  method: "CASH" | "CARD";
  // Delivery only.
  deliveryAddress: string;
  recipientNotes: string;
  priceEur: string;
  packageSize: "" | "SMALL" | "MEDIUM" | "LARGE";
  courierUserId: string;
  distanceKm: string;
  overridePayoutEur: string;
}

export function emptyScheduleDraft(): ScheduleDraft {
  return {
    enabled: false,
    fulfillment: "PICKUP",
    recipientName: "",
    recipientPhone: "",
    when: defaultOrderWhen(),
    payment: "NONE",
    partAmountEur: "",
    method: "CARD",
    deliveryAddress: "",
    recipientNotes: "",
    priceEur: "",
    packageSize: "",
    courierUserId: "",
    distanceKm: "",
    overridePayoutEur: "",
  };
}

/** The delivery fee the order will owe (OD-100), 0 for a pickup. */
function feeEur(d: ScheduleDraft): number {
  if (d.fulfillment !== "COURIER") return 0;
  const fee = Number(d.priceEur);
  return d.priceEur.trim() !== "" && Number.isFinite(fee) ? fee : 0;
}

/** Everything the customer owes: the ticket's goods + the delivery fee. */
export function scheduleOwedEur(d: ScheduleDraft, goodsTotal: number): number {
  return goodsTotal + feeEur(d);
}

/** Money taken at the till now, or undefined when nothing was paid. */
export function schedulePaidNowEur(
  d: ScheduleDraft,
  goodsTotal: number,
): number | undefined {
  // Review fix: nothing owed (a €0 ticket) means nothing is paid — sending
  // 0 € was refused by the server's minimum and stopped the order.
  if (scheduleOwedEur(d, goodsTotal) <= 0) return undefined;
  if (d.payment === "FULL") return scheduleOwedEur(d, goodsTotal);
  if (d.payment === "PART") return Number(d.partAmountEur);
  return undefined;
}

/** The first blocking problem with the schedule form, or null. */
export function validateSchedule(
  d: ScheduleDraft,
  goodsTotal: number,
): string | null {
  if (!d.enabled) return null;
  // Owner ruling 2026-09-26: recipient name AND phone for pickup and delivery.
  if (!d.recipientName.trim()) return translate("courier.addRecipient");
  if (!d.recipientPhone.trim()) return translate("schedule.addRecipientPhone");
  if (d.fulfillment === "COURIER") {
    if (!d.deliveryAddress.trim()) return translate("courier.addAddress");
    const fee = Number(d.priceEur);
    if (!d.priceEur.trim() || !Number.isFinite(fee) || fee < 0) {
      return translate("courier.setFee");
    }
  }
  if (d.payment === "PART" && scheduleOwedEur(d, goodsTotal) > 0) {
    const owed = scheduleOwedEur(d, goodsTotal);
    const part = Number(d.partAmountEur);
    // A part payment is less than the whole; the whole is "Paid in full".
    if (!d.partAmountEur.trim() || !Number.isFinite(part) || part <= 0 || part >= owed) {
      return translate("schedule.setPartAmount", { amount: money(owed) });
    }
  }
  return null;
}

/** The create-order payload for a validated draft (lines come from the ticket). */
export function scheduleToOrderPayload(
  d: ScheduleDraft,
  goodsTotal: number,
  lines: NonNullable<
    Parameters<typeof shopModeApi.floorCreateOrder>[0]["lines"]
  >,
): Parameters<typeof shopModeApi.floorCreateOrder>[0] {
  const paidNow = schedulePaidNowEur(d, goodsTotal);
  const distanceKm = d.distanceKm.trim() ? Number(d.distanceKm) : undefined;
  const overridePayoutEur = d.overridePayoutEur.trim()
    ? Number(d.overridePayoutEur)
    : undefined;
  return {
    // The recipient is the order's contact — the person collecting a pickup,
    // or the one receiving a delivery (the WP-C convention).
    customerName: d.recipientName.trim(),
    customerPhone: d.recipientPhone.trim(),
    fulfillment: d.fulfillment,
    ...orderWhenPayload(d.when),
    totalEur: goodsTotal,
    lines,
    prepaidAmountEur:
      paidNow != null ? Math.round(paidNow * 100) / 100 : undefined,
    prepaidMethod: paidNow != null ? d.method : undefined,
    delivery:
      d.fulfillment === "COURIER"
        ? {
            recipientName: d.recipientName.trim(),
            recipientPhone: d.recipientPhone.trim(),
            recipientNotes: d.recipientNotes.trim() || undefined,
            deliveryAddress: d.deliveryAddress.trim(),
            priceEur: Number(d.priceEur),
            packageSize: d.packageSize || undefined,
            courierUserId: d.courierUserId || undefined,
            distanceKm: Number.isFinite(distanceKm ?? NaN) ? distanceKm : undefined,
            overridePayoutEur: Number.isFinite(overridePayoutEur ?? NaN)
              ? overridePayoutEur
              : undefined,
          }
        : undefined,
  };
}
